import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Network, Loader2, Printer, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ActionBarButton } from "@/components/ActionBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MindmapSvg } from "@/components/MindmapSvg";
import { PublicShareDialog } from "@/components/PublicShareDialog";
import { shareOrigin } from "@/lib/shareText";
import { InfoTip } from "@/components/InfoTip";

const btn = "font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-3 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2";

const svgString = (el) => new XMLSerializer().serializeToString(el);

export function printSvg(el) {
  const w = window.open("", "_blank");
  if (!w) return toast.error(document.documentElement.lang === "en" ? "Allow pop-ups to print." : "Autorisez les fenêtres pour imprimer.");
  w.document.write(`<html><head><title>Carte mentale SUX1</title><style>@page{size:A4 landscape;margin:8mm}body{margin:0}svg{width:100%;height:auto}</style></head><body>${svgString(el)}</body></html>`);
  w.document.close();
  w.onload = () => { w.focus(); w.print(); };
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* already printed */ } }, 600);
}

export function downloadPng(el) {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = 3200; c.height = 2200;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = "carte-mentale-sux1.png";
    a.click();
  };
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString(el));
}

export function MindmapButton({ summaryId }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const svgRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!summaryId) return null;
  const HELP = <><b>{t("mindmap.label", "Carte mentale")}</b>{t("mindmap.colon", " :")} {t("mindmap.help", "SUX1 transforme ce résumé en carte mentale colorée (thème au centre, idées clés et détails autour). 1 crédit la première fois, puis elle se rouvre gratuitement. Imprimez-la en PDF pour la classe ou téléchargez-la en image.")}</>;
  const open = async () => {
    if (data) return setData({ ...data });
    setBusy(true);
    try {
      const { data: r } = await api.post("/mindmap", { summary_id: summaryId });
      setData(r.mindmap);
      setIsPublic(!!r.is_public);
      if (r.charged) toast.success(t("mindmap.created", "Carte mentale créée (1 crédit)."));
    } catch (err) {
      const st = err?.response?.status;
      if (st === 402) toast.error(t("mindmap.need_credit", "Il vous faut 1 crédit pour créer la carte mentale."), { action: { label: t("mindmap.recharge", "Recharger"), onClick: () => navigate("/billing/upgrade") } });
      else if (st === 401) toast.error(t("mindmap.need_account", "Créez un compte gratuit pour faire une carte mentale."));
      else toast.error(err?.response?.data?.detail || t("mindmap.failed", "La création de la carte mentale a échoué."));
    } finally {
      setBusy(false);
    }
  };
  const [shown, setShown] = [Boolean(data), (v) => !v && setData(null)];
  return (
    <div className="mt-8 sm:col-span-2" data-testid="mindmap-create">
      <ActionBarButton icon={busy ? Loader2 : Network} label={busy ? t("mindmap.busy", "Création de la carte…") : t("mindmap.label", "Carte mentale")} onClick={open}
        disabled={busy} chevron={false} testid="mindmap-create-btn" help={HELP}
        tip={t("mindmap.tip", "Carte mentale : transforme ce résumé en carte mentale colorée à imprimer pour la classe.")} />
      <p className="mt-2 text-center text-[11px] text-muted-foreground">
        {t("mindmap.caption", "Le thème au centre, les idées clés en bulles colorées")} · <b>{t("mindmap.cost", "1 crédit")}</b>{t("mindmap.reopen", ", puis gratuite à rouvrir.")}
      </p>
      <Dialog open={shown} onOpenChange={setShown}>
        <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-auto" data-testid="mindmap-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>{t("mindmap.label", "Carte mentale")}</DialogTitle></DialogHeader>
          {data && <MindmapSvg ref={svgRef} data={data} />}
          <div className="flex flex-wrap items-center gap-2 justify-center">
            <button type="button" className={btn} onClick={() => printSvg(svgRef.current)} data-testid="mindmap-print-btn"><Printer className="w-3.5 h-3.5" /> {t("mindmap.print", "Imprimer (PDF)")}</button>
            <button type="button" className={btn} onClick={() => downloadPng(svgRef.current)} data-testid="mindmap-download-btn"><Download className="w-3.5 h-3.5" /> {t("mindmap.download", "Télécharger l'image")}</button>
            <button type="button" className={btn} onClick={() => setShareOpen(true)} data-testid="mindmap-share-btn"><Share2 className="w-3.5 h-3.5" /> {t("mindmap.share", "Partager avec la classe")}</button>
            <InfoTip testid="mindmap-tools-info" content={t("mindmap.tools_tip", "Imprimer : PDF A4 paysage. Télécharger : image PNG haute définition pour un diaporama ou l'ENT. Partager : lien et QR code, les élèves l'ouvrent sans compte. Tout est gratuit une fois la carte créée.")} />
          </div>
        </DialogContent>
      </Dialog>
      <PublicShareDialog open={shareOpen} onOpenChange={setShareOpen} title={t("mindmap.share_title", "Partager la carte mentale avec la classe")}
        url={`${shareOrigin()}/m/${summaryId}`} isPublic={isPublic} testid="mindmap-share"
        onToggle={async (next) => { const { data: r } = await api.post(`/mindmap/${summaryId}/share`, { is_public: next }); setIsPublic(r.is_public); }}
        hint={t("mindmap.share_hint", "Projetez ce QR code : les élèves ouvrent et impriment la carte sur leur téléphone, sans compte.")} />
    </div>
  );
}

export default MindmapButton;
